import { Injectable, signal, inject, computed } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { City } from '../models/models';

@Injectable({
    providedIn: 'root'
})
export class LocationService {
    private supabase = inject(SupabaseService);

    readonly cityObjects = signal<City[]>([
        { id: 'default-1', name: 'Algodonales', latitude: 36.8795, longitude: -5.4055, isEnabled: true },
        { id: 'default-2', name: 'Villamartin', latitude: 36.8617, longitude: -5.6444, isEnabled: true },
        { id: 'default-3', name: 'Ronda', latitude: 36.7462, longitude: -5.1612, isEnabled: true },
        { id: 'default-4', name: 'Olvera', latitude: 36.9328, longitude: -5.2530, isEnabled: true }
    ]);
    readonly cities = computed(() => this.cityObjects().map(c => c.name));

    // Initialize from localStorage if available, otherwise null
    readonly selectedCity = signal<string | null>(null);

    constructor() {
        this.loadCities();
        this.selectedCity.set(this.getStoredCity());
    }

    private async loadCities() {
        try {
            const dbCities = await this.supabase.getCities();
            if (dbCities && dbCities.length > 0) {
                this.cityObjects.set(dbCities);
                // Re-validate stored city after loading from DB
                const current = this.selectedCity();
                if (current && !this.cities().includes(current)) {
                    this.selectedCity.set(null);
                }
            }
        } catch (error) {
            console.error('Error loading cities:', error);
        }
    }

    private getStoredCity(): string | null {
        if (typeof localStorage !== 'undefined') {
            const stored = localStorage.getItem('selectedCity');
            return stored && this.cities().includes(stored) ? stored : null;
        }
        return null;
    }

    selectCity(city: string) {
        if (this.cities().includes(city)) {
            this.selectedCity.set(city);
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('selectedCity', city);
            }
        }
    }

    getNormalizedCity(city: string | null): string {
        if (!city) return '';
        return city
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
    }

    getCityFromNormalized(normalized: string): string | undefined {
        return this.cities().find(c => this.getNormalizedCity(c) === normalized);
    }

    getCityCoordinates(cityName: string): [number, number] | undefined {
        const city = this.cityObjects().find(c => c.name === cityName);
        if (city && city.latitude && city.longitude) {
            return [city.latitude, city.longitude];
        }
        return undefined;
    }
}
